import { backInOut, backOut, circularIn, circularOut } from "@/p5/easings";

// simple --> chaos
const TIME_TO_SHRINK = 4000;
const TIME_TO_PAUSE = 150;
const TIME_TO_EXPAND = 400;
const ROTATION_DELAY = 4400;

// chaos --> simple
const TIME_TO_SIMPLE = 4000;
const SNAPBACK_TIME = 3000;

// there are three "square" sizes in the sketch:
// 1. "large" — used during 100% chaos.
//    scales with window width to ensure we have a good balance of ASCII
//    spread across the screen (not too little or too much.)
// 2. "base" - used during 0% chaos.
//    slightly randomized on each cycle for variety.
// 3. "small" - used during the transition animation from 0% --> 100%.
//    square will shrink to this size before exploding outwards.
//
const SQUARE_SIZE_BASE_MIN = 300;
const SQUARE_SIZE_BASE_MAX = 500;
const SQUARE_SIZE_SMALL = 60;

/**
 * Management class for mapping time to various attributes of the SrcRenderer. This handles animation timing for
 * transitions between chaos <--> simplicity, and some of the things that come with that like square sizing.
 */
class TimeManager {
  constructor(p, enableSquare, lengthSimple, lengthChaos, squareScaleLarge, videoStartMillis) {
    this.p = p;
    this.enableSquare = enableSquare;
    this.nextSnapTime = this.p.millis() + 10000;
    this.currentChaos = 1.0;
    this.targetChaos = 1.0;
    this.rotationOffset = Math.PI / 2;
    this.lastSnapStart = null;
    this.currentRotationZ = 0;
    this.currentRotationY = 0;
    this.videoStartMillis = videoStartMillis;
    this.lengthSimple = lengthSimple;
    this.lengthChaos = lengthChaos;
    this.squareSize = Math.floor(p.random(SQUARE_SIZE_BASE_MIN, SQUARE_SIZE_BASE_MAX));
    this.squareScaleLarge = squareScaleLarge;
  }

  squareSizeLarge() {
    return this.p.constrain(this.p.width, 1440, 2560) * this.squareScaleLarge;
  }

  updateChaos(video) {
    // Switch snap modes if it's time to go from chaos --> simple or vice versa
    if (this.enableSquare && this.p.millis() > this.nextSnapTime) {
      this.nextSnapTime =
        this.p.millis() + (this.targetChaos === 1.0 ? this.lengthSimple : this.lengthChaos);
      this.targetChaos = 1.0 - this.targetChaos;
      this.targetRotationY = this.currentRotationY + (Math.PI - (this.currentRotationY % Math.PI));
      this.lastSnapStart = this.p.millis();

      if (this.targetChaos === 1.0) {
        video.time(this.videoStartMillis);
      } else {
        this.squareSize = Math.floor(this.p.random(SQUARE_SIZE_BASE_MIN, SQUARE_SIZE_BASE_MAX));
      }

      return true;
    }

    if (this.lastSnapStart) {
      let start = 1.0 - this.targetChaos;

      let fn = this.targetChaos === 1.0 ? circularIn : circularOut;

      let startingPoint =
        this.targetChaos === 1.0
          ? this.lastSnapStart + TIME_TO_SHRINK + TIME_TO_PAUSE
          : this.lastSnapStart;

      let num = fn(
        this.p.map(
          this.p.millis(),
          startingPoint,
          this.lastSnapStart +
            (this.targetChaos === 0.0
              ? TIME_TO_SIMPLE
              : TIME_TO_SHRINK + TIME_TO_PAUSE + TIME_TO_EXPAND),
          0.0,
          1.0,
          true
        )
      );

      this.currentChaos = this.p.map(num, 0.0, 1.0, start, this.targetChaos, true);
    }

    this.rotationOffset = 0;
  }

  displacementScale() {
    if (this.targetChaos === 0.0 && this.p.millis() < this.lastSnapStart + TIME_TO_SIMPLE) {
      return Math.abs(
        this.eased(
          backOut,
          this.p.millis(),
          this.lastSnapStart,
          this.lastSnapStart + TIME_TO_SIMPLE,
          0.5,
          0.2
        )
      );
    } else if (this.targetChaos === 0.0) {
      return Math.abs(
        this.p.map(
          this.p.millis(),
          this.lastSnapStart + TIME_TO_SIMPLE,
          this.lastSnapStart + TIME_TO_SIMPLE + SNAPBACK_TIME,
          0.2,
          0.0,
          true
        )
      );
    } else if (
      this.targetChaos === 1.0 &&
      this.lastSnapStart &&
      this.p.millis() < this.lastSnapStart + TIME_TO_SHRINK + TIME_TO_PAUSE
    ) {
      return 0.0;
    } else {
      return 0.5 * this.currentChaos;
    }
  }

  charOffsetScale() {
    return 1.0;
  }

  applyRotation(layer) {
    if (this.targetChaos === 0.0) {
      this.currentRotationY += 0.05 * (this.targetRotationY - this.currentRotationY);
    } else if (this.targetChaos === 1.0 && this.p.millis() < this.lastSnapStart + ROTATION_DELAY) {
      // do nothing pls
    } else if (this.currentChaos < 1.0) {
      this.currentRotationY += 0.2;
    } else {
      this.currentRotationY -= 0.01 * Math.cos(0.005);
    }

    layer.rotateY(this.currentRotationY);

    // layer.rotateY(
    //   -Math.PI *
    //     2 *
    //     this.currentChaos *
    //     Math.cos(this.p.frameCount * 0.005 + this.rotationOffset)
    // );

    if (this.targetChaos === 123124) {
      // this.currentRotationZ += 0.01 * (this.currentRotationZ % Math.PI);
    } else {
      let zScale = this.currentChaos < 0.001 ? 2.0 : 1.0;
      this.currentRotationZ -= 0.01 * zScale;
    }
    layer.rotateZ(this.currentRotationZ + this.rotationOffset * 2);
  }

  wh() {
    if (
      this.targetChaos === 1.0 &&
      this.lastSnapStart &&
      this.p.millis() < this.lastSnapStart + TIME_TO_SHRINK
    ) {
      // Shrink the square to prep for "explosion"
      return this.eased(
        circularIn,
        this.p.millis(),
        this.lastSnapStart,
        this.lastSnapStart + TIME_TO_SHRINK,
        this.squareSize,
        SQUARE_SIZE_SMALL
      );
    } else if (
      this.targetChaos === 1.0 &&
      this.lastSnapStart &&
      this.p.millis() < this.lastSnapStart + TIME_TO_SHRINK + TIME_TO_PAUSE
    ) {
      return SQUARE_SIZE_SMALL;
    } else if (this.targetChaos === 1.0 && this.lastSnapStart) {
      // Go from shrunken square to "explosion"
      return this.eased(
        circularOut,
        this.p.millis(),
        this.lastSnapStart + TIME_TO_SHRINK + TIME_TO_PAUSE,
        this.lastSnapStart + TIME_TO_SHRINK + TIME_TO_PAUSE + TIME_TO_EXPAND,
        SQUARE_SIZE_SMALL,
        this.squareSizeLarge()
      );
    } else if (this.targetChaos === 0.0 && this.p.millis() < this.lastSnapStart + TIME_TO_SIMPLE) {
      return Math.abs(
        this.eased(
          backInOut,
          this.p.millis(),
          this.lastSnapStart,
          this.lastSnapStart + TIME_TO_SIMPLE,
          this.squareSizeLarge(),
          this.squareSize
        )
      );
    } else if (this.targetChaos === 0.0) {
      return this.squareSize;
    } else {
      return this.p.map(this.currentChaos, 0.25, 1, this.squareSize, this.squareSizeLarge(), true);
    }
  }

  dScale() {
    return this.currentChaos;
  }

  eased(fn, mapVal, timeStart, timeEnd, valStart, valEnd) {
    let eVal = this.p.map(mapVal, timeStart, timeEnd, 0, 1, true);
    let easeTime = fn(eVal);
    return this.p.map(easeTime, 0, 1, valStart, valEnd);
  }

  easedHalfStart(fn, mapVal, timeStart, timeEnd, valStart, valEnd) {
    let eVal = this.p.map(mapVal, timeStart, timeEnd, 0, 0.4, true);
    let easeTime = fn(eVal);
    return this.p.map(easeTime, 0, 1, valStart, valEnd);
  }

  easedHalfEnd(fn, mapVal, timeStart, timeEnd, valStart, valEnd) {
    let eVal = this.p.map(mapVal, timeStart, timeEnd, 0.4, 1.0, true);
    let easeTime = fn(eVal);
    return this.p.map(easeTime, 0, 1, valStart, valEnd);
  }
}

export default TimeManager;
